import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useTheme } from '@material-ui/core/styles';
import * as actions from '../../../../../../store';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AddIcon from '@material-ui/icons/Add';
import { useStyles } from '../Interfaces.style';
import NodeCard from './NodeCard';
import { deleteNode, saveNode, pingTestNode, testNode } from './Interfaces.api';
import { getCollectionConfig, getInterfaceTypeConfig, serializeFieldValue } from '../Interfaces.config';
import { DETAIL_RENDERERS } from './CustomRenderers';

const stripInternalFields = (node) => {
  const { _typeId, _nodeType, _submoduleId, ...payload } = node;
  return payload;
};

const NodeList = ({
  moduleId,
  collectionId,
  submoduleId,
  nodes = [],
  onAdd,
  onEdit,
  onDelete,
  onTest,
  onPingTest,
  onRefresh,
  setReportRulesChanged,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [testingNode, setTestingNode] = useState(null);

  const resolvedCollectionId = collectionId || submoduleId;
  const collection = getCollectionConfig(moduleId, resolvedCollectionId);

  if (!collection) {
    return null;
  }

  const emptyMessageKey = collection.emptyMessageKey || `Settings.no_${resolvedCollectionId}_servers_found`;
  const nodesWithType = Array.isArray(nodes)
    ? nodes.map((node) => ({ ...node, _typeId: node._typeId || collection.defaultTypeId }))
    : [];
  const hasNodes = nodesWithType.length > 0;

  const getNodeConfig = (typeId) => {
    return getInterfaceTypeConfig(typeId) || getInterfaceTypeConfig(collection.defaultTypeId);
  };

  const showSaveResult = (response) => {
    dispatch(
      actions.setSnackbar({
        msg: response.success ? t('Settings.saved_successfully') : t(`api_messages.${response.msg}`),
        severity: response.success ? 'success' : 'error',
      })
    );
  };

  const handleDeleteWithType = async (nodeName, typeId) => {
    if (setReportRulesChanged) setReportRulesChanged(true);

    const response = await deleteNode(typeId, nodeName);

    dispatch(
      actions.setSnackbar({
        msg: response.success ? t('Settings.deleted_successfully') : t(`api_messages.${response.msg}`),
        severity: response.success ? 'success' : 'error',
      })
    );

    if (response.success && onRefresh) {
      await onRefresh();
    }

    if (onDelete) {
      onDelete(nodeName);
    }
  };

  const handleTestWithType = async (nodeName, typeId) => {
    const nodeConfig = getNodeConfig(typeId);
    if (!nodeConfig.hasTest) return;

    const testingKey = `${typeId}:${nodeName}`;
    setTestingNode(testingKey);
    try {
      const response = onTest ? await onTest(typeId, nodeName) : await testNode(typeId, nodeName);
      const passed = response?.status === true;

      dispatch(
        actions.setSnackbar({
          msg: response?.msg || (passed ? t('Settings.echo_succeded') : t('Settings.echo_failed')),
          severity: passed ? 'success' : 'error',
        })
      );
    } catch (error) {
      dispatch(
        actions.setSnackbar({
          msg: 'Settings.echo_failed',
          severity: 'error',
        })
      );
    } finally {
      setTestingNode(null);
    }
  };

  const handlePingTestWithType = async (nodeName, typeId) => {
    const nodeConfig = getNodeConfig(typeId);
    if (!nodeConfig.hasPingTest) return;

    const testingKey = `${typeId}:${nodeName}`;
    setTestingNode(testingKey);
    const response = onPingTest ? await onPingTest(typeId, nodeName) : await pingTestNode(typeId, nodeName);
    const isActive = response?.is_active === true || response?.is_active === 1;

    dispatch(
      actions.setSnackbar({
        msg: response?.msg || (isActive ? t('Settings.ping_succeded') : t('Settings.ping_failed')),
        severity: isActive ? 'success' : response?.success ? 'warning' : 'error',
      })
    );
    setTestingNode(null);
  };

  const handleNodeFieldChange = async (node, typeId, fieldKey, newValue) => {
    if (setReportRulesChanged) setReportRulesChanged(true);

    const nodeConfig = getNodeConfig(typeId);
    const field = nodeConfig.fields.find((f) => f.key === fieldKey);
    if (!field) return;

    const payload = stripInternalFields({
      ...node,
      [fieldKey]: serializeFieldValue(field, newValue),
    });

    const response = await saveNode(typeId, payload);
    showSaveResult(response);

    if (response.success && onRefresh) {
      await onRefresh();
    }
  };

  return (
    <Card elevation={1} style={{ marginBottom: 8 }}>
      <CardHeader
        classes={{
          root: classes.cardHeader,
          action: classes.action,
        }}
        disableTypography={true}
        title={
          <div className={classes.mainHeaderContainer}>
            <Typography className={classes.subtitleSettingsCard} variant='h6'>
              {t(collection.titleKey)}
            </Typography>
            <Button
              variant='outlined'
              color='secondary'
              className={classes.buttonGeneral}
              size='small'
              startIcon={<AddIcon />}
              onClick={() => onAdd && onAdd()}>
              {t('Settings.add')}
            </Button>
          </div>
        }
      />
      <CardContent>
        {hasNodes ? (
          nodesWithType.map((node) => {
            const typeId = node._typeId || collection.defaultTypeId;
            const nodeConfig = getNodeConfig(typeId);
            const uniqueKey = `${typeId}-${node.name}`;
            const testingKey = `${typeId}:${node.name}`;
            const customRenderer = DETAIL_RENDERERS[nodeConfig.detailsRenderer];

            return (
              <Accordion
                key={uniqueKey}
                expanded={expandedAccordion === uniqueKey}
                onChange={() => {
                  setExpandedAccordion(expandedAccordion === uniqueKey ? null : uniqueKey);
                }}
                style={{ marginBottom: 16 }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon fontSize='small' />}
                  classes={{
                    root: classes.accordionSummary,
                    content: classes.accordionSummaryContent,
                  }}>
                  <NodeCard
                    node={node}
                    config={nodeConfig}
                    onEdit={() => onEdit && onEdit(node)}
                    onDelete={() => handleDeleteWithType(node.name, typeId)}
                    onToggleFieldChange={(fieldKey, newValue) =>
                      handleNodeFieldChange(node, typeId, fieldKey, newValue)
                    }
                    t={t}
                    classes={classes}
                  />
                </AccordionSummary>
                <AccordionDetails style={{ backgroundColor: '#4C4C4C', flexDirection: 'column', padding: 0 }}>
                  <div style={{ padding: 16 }}>
                    {collection.showType && (
                      <div style={{ marginBottom: 16 }}>
                        <Typography
                          className={classes.nodeTitles}
                          style={{ display: 'inline', marginRight: 8 }}>
                          {t('Settings.report_type')}:
                        </Typography>
                        <Typography className={classes.nodeValues} style={{ display: 'inline' }}>
                          {nodeConfig.typeLabel || String(typeId).toUpperCase()}
                        </Typography>
                      </div>
                    )}
                    {customRenderer ? (
                      customRenderer(node, nodeConfig, t, classes, theme)
                    ) : (
                      <NodeCard.Details node={node} config={nodeConfig} t={t} classes={classes} />
                    )}
                    {(nodeConfig.detailControls || []).map((control) => {
                      const field = nodeConfig.fields.find((f) => f.key === control.key);
                      if (!field) return null;

                      return (
                        <div key={control.key} style={{ marginTop: 16 }}>
                          <Typography className={classes.nodeTitles} style={{ marginBottom: 8 }}>
                            {t(control.titleKey)}
                          </Typography>
                          <FormControl variant='outlined' size='small' fullWidth>
                            <InputLabel>{t(control.labelKey || field.labelKey)}</InputLabel>
                            <Select
                              value={node[control.key] || field.options?.[0]?.value || ''}
                              onChange={(e) =>
                                handleNodeFieldChange(node, typeId, control.key, e.target.value)
                              }
                              label={t(control.labelKey || field.labelKey)}>
                              {(field.options || []).map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {t(option.labelKey)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </div>
                      );
                    })}
                    {nodeConfig.hasTest && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                        <Button
                          variant='outlined'
                          color='secondary'
                          className={classes.buttonGeneral}
                          size='small'
                          onClick={() => handleTestWithType(node.name, typeId)}
                          disabled={testingNode === testingKey}
                          style={{ minWidth: '120px' }}>
                          {testingNode === testingKey && (
                            <CircularProgress size={12} color='secondary' style={{ marginRight: 8 }} />
                          )}
                          {testingNode === testingKey ? t('Settings.echoing') : t('Settings.test_echo')}
                        </Button>
                      </div>
                    )}
                    {nodeConfig.hasPingTest && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                        <Button
                          variant='outlined'
                          color='secondary'
                          className={classes.buttonGeneral}
                          size='small'
                          onClick={() => handlePingTestWithType(node.name, typeId)}
                          disabled={testingNode === testingKey}
                          style={{ minWidth: '120px' }}>
                          {testingNode === testingKey && (
                            <CircularProgress size={12} color='secondary' style={{ marginRight: 8 }} />
                          )}
                          {testingNode === testingKey ? t('Settings.echoing') : t('Settings.ping_test')}
                        </Button>
                      </div>
                    )}
                  </div>
                </AccordionDetails>
              </Accordion>
            );
          })
        ) : (
          <Typography style={{ textAlign: 'center', color: 'gray', fontStyle: 'italic', fontSize: '12px' }}>
            {t(emptyMessageKey)}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default NodeList;
